import {
  Box,
  Button,
  Flex,
  FormControl,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBoolean,
  useMediaQuery,
  VStack,
  useOutsideClick,
  FormLabel,
} from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import FormField from 'components/FormField';
import Reset from 'components/Icons/Reset';
import { InputAdjutment } from 'models/requestBody/getModelOutputs.interface';
import { IndicatorDriver } from 'models/response/driver.interface';
import {
  adjustInput,
  editDriverDataInput,
  resetDriverAdjustmentSlider,
} from 'redux/driverAdjustmentSlice';
import { getModelDetails } from 'redux/PlanPageSlice';
import { fetchAllScenarios, getAllScenarios, getBaseScenario } from 'redux/ScenarioSlice';
import PlanPermission from 'container/PlanPermission';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import ScenarioPermission from 'pages/PermissionComponent/ScenarioPermission';
import { getBlocks } from 'redux/ModelsSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { usePlanPageRevampVariant } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/usePlanPageUiRevamp';
import store from 'redux/store/store';
import { refetchPlanPageFromStore } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageFetchHelpers';

import SliderComponent from './SliderComponent';
import ActionSection from './ActionSection';
import ScenarioDatePicker from './Filters/ScenarioDatePicker';

interface Properties {
  planPermission?: boolean;
  drivers: IndicatorDriver[];
  onToggleAuthModal?: () => void;
  variant?: 'legacy' | 'revamp';
  hideHeader?: boolean;
  embeddedInWhatIfsPanel?: boolean;
}
const validationSchema = Yup.object({
  scenarioName: Yup.string().required('Scenario name is required'),
  scenarioDescription: Yup.string(),
  // startDate: Yup.string().required('StartDate is required'),
});

const EditDriversSection = ({
  drivers,
  planPermission,
  variant = 'legacy',
  hideHeader = false,
  embeddedInWhatIfsPanel = false,
}: Properties) => {
  const isRevamp = usePlanPageRevampVariant(variant);
  const isWhatIfsPanel = isRevamp && embeddedInWhatIfsPanel;
  const themeColors = useSelector(getPlanPageThemeColors);
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [smallLaptop] = useMediaQuery('(max-height: 768px)');
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [isPermissionComponent, setIsPermissionComponent] = useState(false);
  const [startIn, setStartIn] = useState<string>('');
  const [isSelected, setIsSelected] = useBoolean();
  const [popoverSeleceted, setPopoverSeleceted] = useBoolean();
  const [isEditDriverInputsDataChanged, setIsEditDriverInputsDataChanged] = useState(false);
  const [editDriverInputsData, setEditDriverInputsData]: any = useState({});
  const baseScenario = useSelector(getBaseScenario);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const scenarios = useSelector(getAllScenarios);
  const modelDetails = useSelector(getModelDetails);
  const blockDetails = useSelector(getBlocks);
  const blockData = Object.keys(blockDetails).length > 0 ? blockDetails : modelDetails;

  const handleClearSelection = () => {
    setStartIn('');
  };
  useEffect(() => {
    setIsEditDriverInputsDataChanged(true);
  }, [editDriverInputsData]);
  const onAdjustInput = (parameters: InputAdjutment) => {
    setEditDriverInputsData(parameters);
    dispatch(adjustInput(parameters));
  };

  const onResetClick = () => {
    onAdjustInput({});
    setIsResetClicked(true);
  };
  const refetchChartData = () => {
    if (!blockId || !baseScenario?.id) return;
    refetchPlanPageFromStore(dispatch, store.getState, blockId);
  };
  const inputsDriverData = async () => {
    const requestBody = { input_adjustments: editDriverInputsData, scenario_id: baseScenario?.id };
    const data = {
      blockId,
      requestBody,
      toaster: {
        successMessage: `Input is saved to scenario ${baseScenario?.name}`,
        errorMessage: 'Failed to saved data',
      },
    };
    if (isEditDriverInputsDataChanged === true && Object.keys(editDriverInputsData).length > 0) {
      await dispatch(editDriverDataInput(data));
      setIsEditDriverInputsDataChanged(false);
      refetchChartData();
      await dispatch(resetDriverAdjustmentSlider());
      setIsResetClicked(true);
      setPopoverSeleceted.off();
    }
  };

  const handleSubmit = async (values: any, { resetForm }: any) => {
    const requestBody = {
      input_adjustments: editDriverInputsData,
      name: values?.scenarioName,
      description: values?.scenarioDescription,
      scenario_id: baseScenario?.id,
      start_date: values?.startDate,
    };
    const data = {
      blockId,
      requestBody,
      toaster: {
        successMessage: `Input is saved to scenario ${values?.scenarioName}`,
        errorMessage: 'A scenario with this name already exists',
      },
    };
    await dispatch(editDriverDataInput(data));
    setIsEditDriverInputsDataChanged(false);
    setIsResetClicked(true);
    refetchChartData();
    setIsSelected.off();
    setPopoverSeleceted.off();
    await dispatch(resetDriverAdjustmentSlider());
    await dispatch(fetchAllScenarios(modelId));
    resetForm();
    setStartIn('');
  };

  const handleCancel = (resetForm: () => void) => {
    resetForm();
    setIsSelected.off();
    setPopoverSeleceted.off();
    setStartIn('');
  };

  const openPopover = () => {
    if (UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model === null) {
      setIsPermissionComponent(false);
      setIsSelected.on();
    } else if (
      scenarios?.length ===
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model ||
      scenarios?.length >=
        UserSubscriptionLimitData?.subscription_features_limit?.scenario_per_model
    ) {
      setIsSelected.off();
      setIsPermissionComponent(true);
    } else if (planPermission === true) {
      setIsSelected.off();
    } else {
      setIsPermissionComponent(false);
      setIsSelected.on();
    }
  };
  useOutsideClick({
    ref: reference,
    handler: () => {
      setIsSelected.off();
      setIsPermissionComponent(false);
    },
  });

  return (
    <>
      {drivers?.length > 0 && (
        <Box
          mb={isRevamp ? 0 : 5}
          pl={isRevamp ? 0 : 4}
          px={isWhatIfsPanel ? 5 : isRevamp ? 6 : undefined}
          pt={isWhatIfsPanel ? 0 : isRevamp && !hideHeader ? 4 : isRevamp ? 2 : undefined}
          pb={isWhatIfsPanel ? 0 : undefined}
          flex={isWhatIfsPanel ? '1' : undefined}
          display={isWhatIfsPanel ? 'flex' : undefined}
          flexDirection={isWhatIfsPanel ? 'column' : undefined}
          minH={isWhatIfsPanel ? 0 : undefined}
          className='scroll'
        >
          {!hideHeader && (
            <Flex alignItems='center' justifyContent='space-between' mt={isRevamp ? 0 : '3.5'}>
              <Text
                fontFamily={isRevamp ? undefined : 'bold'}
                fontSize={isRevamp ? '16px' : 'lg'}
                fontWeight={isRevamp ? '700' : undefined}
                lineHeight={isRevamp ? '1.2' : '9'}
                color={isRevamp ? themeColors.textDark : undefined}
              >
                Edit inputs
              </Text>
            </Flex>
          )}

          <VStack
            pt={hideHeader && isRevamp ? (isWhatIfsPanel ? 5 : 2) : 4}
            spacing={isRevamp ? 5 : '4'}
            maxH={isRevamp ? undefined : { md: !smallLaptop ? 100 : 40 }}
            pb={isWhatIfsPanel ? 0 : isRevamp ? 2 : '5'}
            mb={isRevamp ? 0 : '5'}
            overflowY={isRevamp ? 'visible' : { md: 'auto' }}
            overflowX='hidden'
            className={isRevamp ? undefined : 'scroll'}
            align='stretch'
            w='100%'
            flex={isWhatIfsPanel ? '1' : undefined}
          >
            {drivers.map((driver) => (
              <SliderComponent
                key={`${driver.id} + ${driver.type}`}
                driver={driver}
                onAdjustInput={onAdjustInput}
                isResetClicked={isResetClicked}
                setIsResetClicked={setIsResetClicked}
                variant={variant}
              />
            ))}
          </VStack>
          <Box
            display='flex'
            justifyContent={isWhatIfsPanel ? 'stretch' : 'space-between'}
            alignItems='center'
            gap={isWhatIfsPanel ? 4 : undefined}
            px={isRevamp ? 0 : undefined}
            pb={isWhatIfsPanel ? 5 : isRevamp ? 4 : undefined}
            pt={isWhatIfsPanel ? 2 : undefined}
            flexShrink={0}
            w='100%'
          >
            <Button
              leftIcon={isWhatIfsPanel ? undefined : <Reset />}
              variant={isWhatIfsPanel ? 'outline' : 'ghost'}
              flex={isWhatIfsPanel ? 1 : undefined}
              w={isWhatIfsPanel ? undefined : 'auto'}
              h={isWhatIfsPanel ? '40px' : 'auto'}
              p={isWhatIfsPanel ? undefined : '0'}
              fontWeight={isRevamp ? '500' : '400'}
              fontSize={isRevamp ? '14px' : '2xs'}
              color={isRevamp ? themeColors.textDark : undefined}
              borderColor={isWhatIfsPanel ? themeColors.border : undefined}
              borderRadius={isWhatIfsPanel ? '10px' : undefined}
              _hover={
                isRevamp
                  ? {
                      color: themeColors.primary,
                      bg: isWhatIfsPanel ? themeColors.activeBg : 'transparent',
                    }
                  : undefined
              }
              onClick={onResetClick}
            >
              Reset
            </Button>
            <Popover
              placement={isWhatIfsPanel ? 'top-end' : 'bottom-start'}
              isOpen={popoverSeleceted}
              onOpen={planPermission ? undefined : setPopoverSeleceted.on}
              onClose={setPopoverSeleceted.off}
              closeOnBlur={true}
              isLazy
              lazyBehavior='keepMounted'
              gutter={8}
            >
              <Box ref={reference} flex={isWhatIfsPanel ? 1 : undefined}>
                <PopoverTrigger>
                  <Button
                    colorScheme={isRevamp ? undefined : 'white'}
                    p={isRevamp ? undefined : '0'}
                    my='0'
                    ms='0'
                    me={isRevamp ? 0 : '2'}
                    height={isWhatIfsPanel ? '40px' : isRevamp ? '36px' : '2rem'}
                    fontWeight={isRevamp ? '600' : '400'}
                    fontSize={isRevamp ? '14px' : 'xs'}
                    textTransform='capitalize'
                    borderRadius={isWhatIfsPanel ? '10px' : isRevamp ? '10px' : '8px'}
                    width={isWhatIfsPanel ? '100%' : isRevamp ? '72px' : '65px'}
                    flex={isWhatIfsPanel ? 1 : undefined}
                    boxShadow={isRevamp ? 'none' : 'none !important'}
                    bg={isRevamp ? themeColors.primary : undefined}
                    color={isRevamp ? themeColors.white : undefined}
                    _hover={isRevamp ? { bg: themeColors.primaryHover } : undefined}
                    _active={isRevamp ? { bg: themeColors.primaryPressed } : undefined}
                    cursor={planPermission ? 'not-allowed' : 'pointer'}
                    isDisabled={planPermission}
                  >
                    Save
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  color={isRevamp ? themeColors.textDark : 'black'}
                  bg={isRevamp ? themeColors.surface : 'white'}
                  borderColor={isRevamp ? themeColors.border : '#7163D0'}
                  borderRadius={isRevamp ? '10px' : '11px'}
                  width={isWhatIfsPanel ? '240px' : 'fit-content'}
                  p={0}
                  m={0}
                  boxShadow={isRevamp ? 'md' : undefined}
                  zIndex='popover'
                >
                  <Button
                    borderBottom={0}
                    color={isRevamp ? themeColors.textDark : '#212121'}
                    bg={isRevamp ? themeColors.surface : 'white'}
                    height={isRevamp ? '40px' : '2.5rem'}
                    _hover={{ bg: isRevamp ? themeColors.activeBg : '#EFEEFE' }}
                    borderRadius={isRevamp ? '8px' : '5px'}
                    width='100%'
                    onClick={inputsDriverData}
                    textDecoration='none'
                    fontFamily={isRevamp ? undefined : 'regular_roboto'}
                    fontSize={isRevamp ? '14px' : '14px'}
                    fontWeight='500'
                    textTransform='none'
                  >
                    Save to this scenario
                  </Button>
                  <PopoverBody m={0} p={0}>
                    <Popover
                      placement={isWhatIfsPanel ? 'left-start' : 'left-start'}
                      isOpen={isSelected}
                      onOpen={openPopover}
                      onClose={setIsSelected.off}
                      closeOnBlur={true}
                      isLazy
                      lazyBehavior='keepMounted'
                      gutter={12}
                    >
                      <PopoverTrigger>
                        <Button
                          color={isRevamp ? themeColors.textDark : '#212121'}
                          cursor='pointer'
                          alignSelf='center'
                          height={isRevamp ? '40px' : '2.5rem'}
                          _hover={{ bg: isRevamp ? themeColors.activeBg : '#EFEEFE' }}
                          borderRadius={isRevamp ? '8px' : '5px'}
                          width='100%'
                          textDecoration='none'
                          fontFamily={isRevamp ? undefined : 'regular_roboto'}
                          fontSize='14px'
                          fontWeight='500'
                          bg={
                            isSelected
                              ? isRevamp
                                ? themeColors.activeBg
                                : '#EFEEFE'
                              : isRevamp
                              ? themeColors.surface
                              : 'white'
                          }
                          textTransform='none'
                        >
                          Save to new scenario
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        bg={isRevamp ? themeColors.surface : 'white'}
                        color={isRevamp ? themeColors.textDark : '#212121'}
                        p={4}
                        width='260px'
                        borderRadius={isRevamp ? '10px' : undefined}
                        borderColor={isRevamp ? themeColors.border : undefined}
                      >
                        <Box>
                          <Text
                            color={isRevamp ? themeColors.primary : '#6562CF'}
                            fontSize={isRevamp ? '14px' : { base: 'xs', md: 'sm' }}
                            fontWeight='600'
                            mb={2}
                          >
                            Enter Details
                          </Text>
                          <Formik
                            initialValues={{
                              scenarioName: '',
                              scenarioDescription: '',
                              startDate: '',
                            }}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                          >
                            {({ errors, touched, resetForm, setFieldValue }) => (
                              <Form>
                                <FormControl>
                                  <FormField
                                    label='Name'
                                    name='scenarioName'
                                    type='text'
                                    labelStyles={{
                                      fontSize: '0.8rem',
                                      color: '#212121',
                                      fontWeight: '600',
                                      opacity: 0.8,
                                    }}
                                    inputStyles={{
                                      maxWidth: '300px',
                                      height: '2.3rem',
                                      boxShadow: '0px 0px 0px 0.6px #8B89B1',
                                      fontSize: '0.8rem',
                                      color: '#212121',
                                    }}
                                    placeholder='Name'
                                    error={errors.scenarioName}
                                    touched={touched.scenarioName}
                                  />
                                </FormControl>

                                <FormControl mt={4}>
                                  <FormField
                                    label='Description'
                                    name='scenarioDescription'
                                    type='text'
                                    labelStyles={{
                                      fontSize: '0.8rem',
                                      color: '#212121',
                                      fontWeight: '600',
                                      opacity: 0.8,
                                    }}
                                    inputStyles={{
                                      maxWidth: '300px',
                                      fontSize: '0.8rem',
                                      color: '#212121',
                                      height: '2.3rem',
                                      boxShadow: '0px 0px 0px 0.6px #8B89B1',
                                    }}
                                    placeholder='Description'
                                  />
                                </FormControl>
                                <FormControl mt={4}>
                                  <FormLabel
                                    fontSize='0.9rem'
                                    color='black'
                                    htmlFor='selecetDimension'
                                    fontFamily={'Roboto Regular'}
                                  >
                                    Forecast start date
                                  </FormLabel>
                                  <Flex bgColor={'white'}>
                                    <ScenarioDatePicker
                                      clearSection={false}
                                      clearDateInput={handleClearSelection}
                                      selected={startIn ? new Date(startIn) : null}
                                      onChange={(date: Date) => {
                                        setStartIn(date.toDateString());
                                        setFieldValue('startDate', date.toDateString());
                                      }}
                                      timescale={
                                        blockData?.time_properties?.time_granularity === 'Y'
                                          ? 'Y'
                                          : 'M'
                                      }
                                    />
                                  </Flex>
                                  <ErrorMessage
                                    name='startDate'
                                    component='div'
                                    className='error'
                                  />
                                </FormControl>

                                <Flex
                                  mt={4}
                                  color={'black.900'}
                                  fontSize='xs'
                                  display={'flex'}
                                  justifyContent={'flex-end'}
                                  alignContent={'center'}
                                >
                                  <Button
                                    type='reset'
                                    onClick={() => handleCancel(resetForm)}
                                    width={'80px'}
                                    height={'30px'}
                                    borderRadius='xs'
                                    fontSize='xs'
                                    bgColor='#EFEEFE'
                                    color='#6562CF'
                                    fontWeight='400'
                                    me={'1rem'}
                                    fontFamily={'Roboto Regular'}
                                    px='10'
                                    py='4'
                                    textTransform='capitalize'
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type='submit'
                                    width={'80px'}
                                    height={'30px'}
                                    borderRadius='xs'
                                    fontSize='xs'
                                    bgColor='#6562CF'
                                    fontWeight='400'
                                    fontFamily={'Roboto Regular'}
                                    px='10'
                                    py='4'
                                    textTransform='capitalize'
                                  >
                                    Save
                                  </Button>
                                </Flex>
                              </Form>
                            )}
                          </Formik>
                        </Box>
                      </PopoverContent>
                    </Popover>
                    {isPermissionComponent && (
                      <Flex px={1}>
                        <ScenarioPermission
                          ScenarioLimit={
                            UserSubscriptionLimitData?.subscription_features_limit
                              ?.scenario_per_model
                          }
                          CurrentScenarioLength={scenarios?.length}
                        />
                      </Flex>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </Box>
            </Popover>
          </Box>
          {!isRevamp && (
            <Box display={{ base: 'none', md: 'block' }}>
              <ActionSection />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default PlanPermission(EditDriversSection);
